import React from 'react';
import { i18n } from 'config/locales';
import ValidationElement from 'components/Form/ValidationElement';
import Field from 'components/Form/Field';
import DateControl from 'components/Form/DateControl';
import Currency from 'components/Form/Currency';
import Checkbox from 'components/Form/Checkbox';
import Branch from 'components/Form/Branch';
import Textarea from 'components/Form/Textarea';
import Layout from 'components/Form/PVQLocation/Layouts';
import Text from 'components/Form/Text';
import PVQName from 'components/Form/PVQName';
import Show from 'components/Form/Show';
import { ValidationElementProps } from 'components/Form/ValidationElement/ValidationElement';
import {
  CommonOnUpdate,
  CommonOnError,
  CommonRequired,
  CommonScrollIntoView,
} from 'types/props';
import PVQLocation from 'components/Form/PVQLocation';
import RadioGroup from 'components/Form/RadioGroup';
import Radio from 'components/Form/Radio';
import { evictionEntityTypes } from 'constants/enums/financialOptions';
import NotApplicable from 'components/Form/NotApplicable';
import { FinancialEvictionItemModel } from 'forms/sections/financial/eviction/types';

interface Props extends Partial<FinancialEvictionItemModel>, ValidationElementProps {
  onUpdate?: CommonOnUpdate
  onError?: CommonOnError
  required?: CommonRequired
  scrollIntoView?: CommonScrollIntoView
}

export default class EvictionItem extends ValidationElement<Props> {
  update = (queue: Partial<FinancialEvictionItemModel>) => {
    this.props.onUpdate({
      EntityType: this.props.EntityType,
      EntityTypeExplain: this.props.EntityTypeExplain,
      EntityName: this.props.EntityName,
      PrivateName: this.props.PrivateName,
      PropertyLocation: this.props.PropertyLocation,
      HasMissedRent: this.props.HasMissedRent,
      FirstPastDue: this.props.FirstPastDue,
      NumberMissed: this.props.NumberMissed,
      NumberMissedEstimated: this.props.NumberMissedEstimated,
      AmountOwed: this.props.AmountOwed,
      AmountOwedEstimated: this.props.AmountOwedEstimated,
      Why: this.props.Why,
      When: this.props.When,
      Current: this.props.Current,
      WhenResolved: this.props.WhenResolved,
      ActionsTaken: this.props.ActionsTaken,
      ActionsTakenNone: this.props.ActionsTakenNone,
      ActionsTakenReason: this.props.ActionsTakenReason,
      CourtName: this.props.CourtName,
      CourtNameNotApplicable: this.props.CourtNameNotApplicable,
      CourtLocation: this.props.CourtLocation,
      OrderNum: this.props.OrderNum,
      OrderNumIdk: this.props.OrderNumIdk,
      ...queue,
    });
  };

  updateWhenResolved = (values: FinancialEvictionItemModel['WhenResolved']) => {
    this.update({
      WhenResolved: values,
    });
  };

  updateEntityType = (values: FinancialEvictionItemModel['EntityType']) => {
    this.update({
      EntityType: values,
    });
  };

  updateEntityTypeExplain = (values: FinancialEvictionItemModel['EntityTypeExplain']) => {
    this.update({
      EntityTypeExplain: values,
    });
  };

  updateEntityName = (values: FinancialEvictionItemModel['EntityName']) => {
    this.update({
      EntityName: values,
    });
  };

  updatePrivateName = (values: FinancialEvictionItemModel['PrivateName']) => {
    this.update({
      PrivateName: values,
    });
  };

  updatePropertyLocation = (values: FinancialEvictionItemModel['PropertyLocation']) => {
    this.update({
      PropertyLocation: values,
    });
  };

  updateHasMissedRent = (values: FinancialEvictionItemModel['HasMissedRent']) => {
    const hasMissedRent = values?.value === 'Yes';
    this.update({
      HasMissedRent: values,
      FirstPastDue: hasMissedRent ? this.props.FirstPastDue : {} as FinancialEvictionItemModel['FirstPastDue'],
      NumberMissed: hasMissedRent ? this.props.NumberMissed : {} as FinancialEvictionItemModel['NumberMissed'],
      AmountOwed: hasMissedRent ? this.props.AmountOwed : {} as FinancialEvictionItemModel['AmountOwed'],
      AmountOwedEstimated: hasMissedRent ? this.props.AmountOwedEstimated : {} as FinancialEvictionItemModel['AmountOwedEstimated'],
      Why: hasMissedRent ? this.props.Why : {} as FinancialEvictionItemModel['Why'],
    });
  };

  updateFirstPastDue = (values: FinancialEvictionItemModel['FirstPastDue']) => {
    this.update({
      FirstPastDue: values,
    });
  };

  updateNumberMissed = (values: FinancialEvictionItemModel['NumberMissed']) => {
    this.update({
      NumberMissed: values,
    });
  };

  updateAmountOwed = (values: FinancialEvictionItemModel['AmountOwed']) => {
    this.update({
      AmountOwed: values,
    });
  };

  updateAmountOwedEstimated = (values: FinancialEvictionItemModel['AmountOwedEstimated']) => {
    this.update({
      AmountOwedEstimated: values,
    });
  };

  updateWhy = (values: FinancialEvictionItemModel['Why']) => {
    this.update({
      Why: values,
    });
  };

  updateWhen = (values: FinancialEvictionItemModel['When']) => {
    this.update({
      When: values,
    });
  };

  updateCourtName = (values: FinancialEvictionItemModel['CourtName']) => {
    this.update({
      CourtName: values,
    });
  };

  updateCourtNameNotApplicable = (values: FinancialEvictionItemModel['CourtNameNotApplicable']) => {
    this.update({
      CourtNameNotApplicable: values,
    });
  };

  updateCourtLocation = (values: FinancialEvictionItemModel['CourtLocation']) => {
    this.update({
      CourtLocation: values,
    });
  };

  updateOrderNum = (values: FinancialEvictionItemModel['OrderNum']) => {
    this.update({
      OrderNum: values,
    });
  };

  updateOrderNumIdk = (values: FinancialEvictionItemModel['OrderNumIdk']) => {
    this.update({
      OrderNumIdk: values,
    });
  };

  updateCurrent = (values: FinancialEvictionItemModel['Current']) => {
    this.update({
      Current: values,
    });
  };

  updateNumberMissedEstimated = (values: FinancialEvictionItemModel['NumberMissedEstimated']) => {
    this.update({
      NumberMissedEstimated: values,
    });
  };

  updateActionsTaken = (values: FinancialEvictionItemModel['ActionsTaken']) => {
    this.update({
      ActionsTaken: values,
    });
  };

  updateActionsTakenNone = (values: FinancialEvictionItemModel['ActionsTakenNone']) => {
    this.update({
      ActionsTakenNone: values,
    });
  };

  updateActionsTakenReason = (values: FinancialEvictionItemModel['ActionsTakenReason']) => {
    this.update({
      ActionsTakenReason: values,
    });
  };

  render() {
    const attribute = `List.items[${this.props.$index}].Item`;

    return (
      <div className="eviction-item">
        <Field
          title={i18n.t('financial.pvq.eviction.branch.ifYes.entityTypeRadioGroup.title')}
          titleSize="h4"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.EntityType`}
        >
          <RadioGroup
            {...this.props.EntityType}
            className="option-list option-list-vertical"
            selectedValue={(this.props.EntityType || {}).value}
            required={this.props.required}
            onError={this.props.onError}
            attribute={`${attribute}.EntityTypeRadioGroup`}
          >
            {evictionEntityTypes.map((entityType) => (
              <Radio
                key={entityType}
                label={entityType}
                value={entityType}
                onUpdate={this.updateEntityType}
              />
            ))}
          </RadioGroup>
        </Field>

        {(
          this.props.EntityType?.value === 'Company'
          || this.props.EntityType?.value === 'Government Agency'
        ) && (
        <Field
          title={i18n.t('financial.pvq.eviction.branch.ifYes.entityTypeRadioGroup.ifCompanyAgencyOrEntityText')}
          titleSize="h4"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.EntityName`}
        >
          <Text
            {...this.props.EntityName}
            attribute={`${attribute}.EntityName`}
            name="entityname"
            onUpdate={this.updateEntityName}
            onError={this.props.onError}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
          />
        </Field>
        )}

        {this.props.EntityType?.value === 'Other' && (
          <Field
            title={i18n.t('financial.pvq.eviction.branch.ifYes.entityTypeRadioGroup.ifOtherTextArea')}
            titleSize="h4"
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.EntityTypeExplain`}
          >
            <Textarea
              {...this.props.EntityTypeExplain}
              attribute={`${attribute}.EntityTypeExplain`}
              name="entitytypeexplain"
              onUpdate={this.updateEntityTypeExplain}
              onError={this.props.onError}
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
            />
          </Field>
        )}
        {this.props.EntityType?.value === 'Individual' && (
          <Field
            title={i18n.t('financial.pvq.eviction.branch.ifYes.entityTypeRadioGroup.ifPrivateText')}
            titleSize="h4"
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.PrivateName`}
          >
            <PVQName
              {...this.props.PrivateName}
              attribute={`${attribute}.PrivateName`}
              name="privatename"
              onUpdate={this.updatePrivateName}
              onError={this.props.onError}
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
              showLastFirstMiddleNameOrder
            />
          </Field>
        )}

        <Field
          title={i18n.t('financial.pvq.eviction.branch.ifYes.propertyLocation')}
          titleSize="h4"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.PropertyLocation`}
        >
          <PVQLocation
            {...this.props.PropertyLocation}
            attribute={`${attribute}.PropertyLocation`}
            name="propertylocation"
            layout={Layout.US_STREET_CITY_STATE_ZIP}
            onUpdate={this.updatePropertyLocation}
            onError={this.props.onError}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
          />
        </Field>

        <Branch
          name="has_missed_rent"
          label={i18n.t('financial.pvq.eviction.branch.ifYes.hasMissedRent')}
          labelSize="h4"
          className="has-missed-rent"
          {...this.props.HasMissedRent}
          warning
          onUpdate={this.updateHasMissedRent}
          required={this.props.required}
          scrollIntoView={this.props.scrollIntoView}
          onError={this.props.onError}
          attribute="HasMissedRent"
        />

        <Show when={this.props.HasMissedRent?.value === 'Yes'}>

          <Field
            title={i18n.t('financial.pvq.eviction.branch.ifYes.missedPayments.whenFirstDateEstimated')}
            titleSize="h4"
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.FirstPastDue`}
          >
            <DateControl
              {...this.props.FirstPastDue}
              attribute={`${attribute}.FirstPastDue`}
              name="firstpastdue"
              onUpdate={this.updateFirstPastDue}
              hideDay
              onError={this.props.onError}
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
            />
          </Field>

          <Field
            title={i18n.t('financial.pvq.eviction.branch.ifYes.missedPayments.numberMissedText')}
            titleSize="h4"
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.NumberMissed`}
          >
            <Text
              {...this.props.NumberMissed}
              attribute={`${attribute}.NumberMissed`}
              name="nummissed"
              onUpdate={this.updateNumberMissed}
              onError={this.props.onError}
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
            />
            <Checkbox
              name="nummissedstimated"
              label={i18n.t('financial.pvq.shared.estimated')}
              {...this.props.NumberMissedEstimated}
              onUpdate={this.updateNumberMissedEstimated}
              onError={this.props.onError}
              attribute={`${this.props.attribute}.NumberMissedEstimated`}
            />
          </Field>

          <Field
            title={i18n.t('financial.pvq.eviction.branch.ifYes.amountOwedTextEstimated')}
            titleSize="h4"
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.AmountOwed`}
          >
            <Currency
              maxLength="10"
              {...this.props.AmountOwed}
              attribute={`${attribute}.AmountOwed`}
              name="amountowed"
              onUpdate={this.updateAmountOwed}
              onError={this.props.onError}
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
            />
            <Checkbox
              name="amountowedestimated"
              label={i18n.t('financial.pvq.shared.estimated')}
              {...this.props.AmountOwedEstimated}
              onUpdate={this.updateAmountOwedEstimated}
              onError={this.props.onError}
              attribute={`${this.props.attribute}.AmountOwedEstimated`}
            />
          </Field>

          <Field
            title={i18n.t('financial.pvq.eviction.branch.ifYes.whyTextArea')}
            titleSize="h4"
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.Why`}
          >
            <Textarea
              {...this.props.Why}
              className="option-list option-list-vertical"
              selectedValue={(this.props.Why || {}).value}
              required={this.props.required}
              onUpdate={this.updateWhy}
              onError={this.props.onError}
              attribute={`${attribute}.Why`}
            />
          </Field>
        </Show>
        <Field
          title={i18n.t('financial.pvq.eviction.branch.ifYes.whenDateEstimated')}
          titleSize="h4"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.When`}
        >
          <DateControl
            {...this.props.When}
            attribute={`${attribute}.When`}
            name="when"
            onUpdate={this.updateWhen}
            onError={this.props.onError}
            required={this.props.required}
            hideDay
            scrollIntoView={this.props.scrollIntoView}
          />
        </Field>

        <Branch
          {...this.props.Current}
          name="current"
          label={i18n.t('financial.pvq.eviction.branch.ifYes.branch.title')}
          labelSize="h4"
          className="eviction-current-branch"
          onUpdate={this.updateCurrent}
          required={this.props.required}
          scrollIntoView={this.props.scrollIntoView}
          onError={this.handleError}
          attribute={`${attribute}.Current`}
        />

        {this.props.Current?.value === 'No' && (
          <Field
            title={i18n.t('financial.pvq.eviction.branch.ifYes.branch.ifNo.whenDateEstimated')}
            titleSize="h4"
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.WhenResolved`}
          >
            <DateControl
              {...this.props.WhenResolved}
              attribute={`${attribute}.WhenResolved`}
              name="whenresolved"
              onUpdate={this.updateWhenResolved}
              onError={this.props.onError}
              hideDay
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
              showEstimated
            />
          </Field>
        )}

        {this.props.Current?.value === 'Yes' && (
          <Field
            title={i18n.t('financial.pvq.eviction.branch.ifYes.branch.ifYes.combobox.actionsTaken')}
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.ActionsTaken`}
          >
            <NotApplicable
              {...this.props.ActionsTakenNone}
              label={i18n.t('financial.pvq.shared.none')}
              attribute={`${attribute}.ActionsTakenNone`}
              onUpdate={this.updateActionsTakenNone}
              scrollIntoView={this.props.scrollIntoView}
              required={this.props.required}
            >
              <Textarea
                {...this.props.ActionsTaken}
                attribute={`${attribute}.ActionsTaken`}
                scrollIntoView={this.props.scrollIntoView}
                onError={this.props.onError}
                onUpdate={this.updateActionsTaken}
                required={this.props.required}
              />
            </NotApplicable>
          </Field>
        )}

        {this.props.ActionsTakenNone?.applicable === false && this.props.Current?.value === 'Yes' && (
          <Field
            title={i18n.t('financial.pvq.eviction.branch.ifYes.branch.ifYes.combobox.ifNoneTextArea')}
            titleSize="label"
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.ActionsTakenReason`}
          >
            <Textarea
              {...this.props.ActionsTakenReason}
              attribute={`${attribute}.ActionsTakenReason`}
              scrollIntoView={this.props.scrollIntoView}
              onUpdate={this.updateActionsTakenReason}
              onError={this.props.onError}
              required={this.props.required}
            />
          </Field>
        )}

        <Field
          title={i18n.t('financial.pvq.eviction.branch.ifYes.courtNameTextNotApplicable')}
          titleSize="h4"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.CourtName`}
        >
          <NotApplicable
            {...this.props.CourtNameNotApplicable}
            label={i18n.t('financial.pvq.shared.na')}
            onUpdate={this.updateCourtNameNotApplicable}
            attribute={`${attribute}.CourtNameNotApplicable`}
          >
            <Text
              {...this.props.CourtName}
              attribute={`${attribute}.CourtName`}
              name="courtname"
              onUpdate={this.updateCourtName}
              onError={this.props.onError}
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
            />
          </NotApplicable>
        </Field>

        <Field
          title={i18n.t('financial.pvq.eviction.branch.ifYes.courtLocation')}
          titleSize="h4"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.CourtLocation`}
        >
          <PVQLocation
            {...this.props.CourtLocation}
            attribute={`${attribute}.CourtLocation`}
            name="courtlocation"
            layout={Layout.US_STREET_CITY_STATE_ZIP}
            onUpdate={this.updateCourtLocation}
            onError={this.props.onError}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
          />
        </Field>

        <Field
          title={i18n.t('financial.pvq.eviction.branch.ifYes.orderNumTextIdk')}
          titleSize="h4"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.OrderNum`}
        >
          <NotApplicable
            {...this.props.OrderNumIdk}
            name="ordernumidk"
            label={i18n.t('financial.pvq.shared.idk')}
            onUpdate={this.updateOrderNumIdk}
            onError={this.props.onError}
            attribute={`${attribute}.OrderNumIdk`}
          >
            <Text
              {...this.props.OrderNum}
              attribute={`${attribute}.OrderNum`}
              name="ordernum"
              onUpdate={this.updateOrderNum}
              onError={this.props.onError}
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
            />
          </NotApplicable>
        </Field>
      </div>
    );
  }
}
EvictionItem.defaultProps = {
  onUpdate: (queue) => { },
  onError: (value, arr) => arr,
  required: false,
};
